
import { useState, useEffect, useCallback, memo } from "react";
import { CSVLink } from "react-csv";
import { useNavigate } from "react-router-dom";
import AnimacionCarga from "../animacion_de_carga";
import TablaGenerica from "../tablas";
import BarraSuperior from "../barra_superior";
import { crearRegistro, actualizarRegistro, eliminarRegistro } from "../../../functions/crud";
import {
  RUTA_CATEGORIAS, CORREO_SOPORTE, cargarCategorias, fechaLegible, inicialesDe,
  Modal, Campo, useConfirmacion, useAviso,
} from "./comunes";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../css/tickets.css";

const COLUMNAS = [
  { label: "Categoría", key: "descripcion_categoria" },
  { label: "Creada por", key: "nombre_funcionario" },
  { label: "Fecha de creación", key: "fecha_creacion" },
];


const ModalCategoria = ({ categoria, alGuardar, alCerrar }) => {
  const [descripcion, setDescripcion] = useState(categoria?.descripcion_categoria || "");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const enviar = async () => {
    if (!descripcion.trim()) return setError("Escribe el nombre de la categoría.");
    setError("");
    setGuardando(true);
    try {
      await alGuardar(descripcion.trim());
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      titulo={categoria ? "Editar categoría" : "Nueva categoría"}
      subtitulo="Las categorías son las que se pueden elegir al crear un ticket"
      ancho="sm"
      alCerrar={alCerrar}
      pie={
        <>
          {error && <span style={{ color: "#b3261e", fontSize: "0.8rem" }}>{error}</span>}
          <div className="tk-derecha">
            <button type="button" className="tk-btn" onClick={alCerrar} disabled={guardando}>Cancelar</button>
            <button type="button" className="tk-btn tk-btn-primario" onClick={enviar} disabled={guardando}>
              {guardando ? "Guardando…" : categoria ? "Guardar" : "Crear"}
            </button>
          </div>
        </>
      }
    >
      <Campo etiqueta="Nombre de la categoría">
        <input
          type="text"
          autoFocus
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && enviar()}
          placeholder="Ej. Soporte de equipos"
        />
      </Campo>
    </Modal>
  );
};


const ListadoCategoriasTickets = memo(() => {
  const navegar = useNavigate();

  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modal, setModal] = useState(null);

  const { elemento: confirmacion, pedirConfirmacion } = useConfirmacion();
  const { elemento: aviso, avisar } = useAviso();

  const correoActual = localStorage.getItem("CorreoElectronico") || "";
  const esSoporte = correoActual === CORREO_SOPORTE;

  const capturarDatos = useCallback(async () => {
    try {
      setDatos(await cargarCategorias());
    } catch (error) {
      console.error("Error al cargar categorías:", error);
      avisar("No se pudieron cargar las categorías", "error");
    } finally {
      setCargando(false);
    }
  }, [avisar]);

  useEffect(() => { capturarDatos(); }, [capturarDatos]);

  const guardar = useCallback(async (descripcion, categoria) => {
    try {
      if (categoria) {
        const { id, ...resto } = categoria;
        await actualizarRegistro(`${RUTA_CATEGORIAS}/${id}`, {
          ...resto,
          descripcion_categoria: descripcion,
        });
        avisar("Categoría actualizada");
      } else {
        await crearRegistro(RUTA_CATEGORIAS, {
          descripcion_categoria: descripcion,
          nombre_funcionario: correoActual,
          fecha_creacion: new Date().toISOString(),
        });
        avisar("Categoría creada");
      }
      setModal(null);
      capturarDatos();
    } catch (error) {
      console.error("Error al guardar la categoría:", error);
      avisar("No se pudo guardar la categoría", "error");
    }
  }, [correoActual, avisar, capturarDatos]);

  const eliminar = useCallback((categoria) => {
    pedirConfirmacion({
      titulo: "¿Eliminar categoría?",
      mensaje: (
        <>
          Se eliminará <strong>{categoria.descripcion_categoria}</strong>. Los tickets ya creados
          conservan el nombre de la categoría, pero dejará de aparecer al crear tickets nuevos.
        </>
      ),
      alConfirmar: async () => {
        try {
          await eliminarRegistro(`${RUTA_CATEGORIAS}/${categoria.id}`);
          avisar("Categoría eliminada");
          capturarDatos();
        } catch (error) {
          console.error("Error al eliminar la categoría:", error);
          avisar("No se pudo eliminar la categoría", "error");
        }
      },
    });
  }, [pedirConfirmacion, avisar, capturarDatos]);

  if (cargando) return <AnimacionCarga />;

  if (!esSoporte) {
    return (
      <div className="tk">
        <BarraSuperior />
        <div className="tk-cabecera-modulo">
          <div className="tk-vacio" style={{ padding: 60 }}>
            <i className="bi bi-lock" />
            Solo el área de soporte puede administrar las categorías de tickets.
          </div>
        </div>
      </div>
    );
  }

  const renderizadores = {
    descripcion_categoria: (valor) => (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
            background: "linear-gradient(135deg,#e74c3c,#c0392b)", color: "#fff",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: "0.7rem", fontWeight: 700,
          }}
        >
          {inicialesDe(valor)}
        </span>
        <strong>{valor}</strong>
      </span>
    ),
    fecha_creacion: (valor) => fechaLegible(valor),
    nombre_funcionario: (valor) => valor || "—",
  };

  return (
    <div className="tk">
      <BarraSuperior />

      <div className="tk-cabecera-modulo">
        <div className="tk-encabezado">
          <div>
            <h1>Categorías de tickets</h1>
            <p>{datos.length} categoría{datos.length === 1 ? "" : "s"} disponible{datos.length === 1 ? "" : "s"} al crear un ticket</p>
          </div>

          <div className="tk-acciones">
            <button type="button" className="tk-btn" onClick={() => navegar("/tickets")}>
              <i className="bi bi-arrow-left" /> Tickets
            </button>
            <button type="button" className="tk-btn tk-btn-icono" title="Refrescar" onClick={capturarDatos}>
              <i className="bi bi-arrow-clockwise" />
            </button>
            <CSVLink
              data={datos}
              headers={COLUMNAS}
              filename="categorias_tickets.csv"
              separator=";"
              className="tk-btn"
              title="Exportar a CSV"
            >
              <i className="bi bi-download" /> Exportar
            </CSVLink>
            <button type="button" className="tk-btn tk-btn-primario" onClick={() => setModal({})}>
              <i className="bi bi-plus-lg" /> Nueva categoría
            </button>
          </div>
        </div>
      </div>

      <TablaGenerica
        datos={datos}
        titulos={COLUMNAS.map((c) => c.label.toUpperCase())}
        campos={COLUMNAS.map((c) => c.key)}
        elementosPorPagina={50}
        columnaOrdenInicial={0}
        direccionOrdenInicial="asc"
        renderizadores={renderizadores}
        mostrarBarraSuperior={false}
        botones={[
          { texto: "Editar", icono: "bi-pencil", accion: (categoria) => setModal({ categoria }) },
          { texto: "Eliminar", icono: "bi-trash", accion: eliminar },
        ]}
      />

      {modal && (
        <ModalCategoria
          categoria={modal.categoria}
          alCerrar={() => setModal(null)}
          alGuardar={(descripcion) => guardar(descripcion, modal.categoria)}
        />
      )}

      {confirmacion}
      {aviso}
    </div>
  );
});

export default ListadoCategoriasTickets;
